import type { CSSProperties } from 'react';
import type { Extensions } from '@tiptap/core';
import { Table as BaseTable, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import type { ResolvedPos } from '@tiptap/pm/model';
import type { EditorState } from '@tiptap/pm/state';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { CellSelection, tableEditingKey, TableMap } from '@tiptap/pm/tables';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { getDocumentTableTheme } from '@/shared/components/tables/DocumentTable/theme';
import { getResizeHandleGradient } from '@/shared/styles';

const backgroundColorAttribute = {
    default: null,
    parseHTML: (element: HTMLElement) =>
        element.getAttribute('data-background-color') || element.style.backgroundColor || null,
    renderHTML: (attributes: Record<string, unknown>) => {
        const backgroundColor = typeof attributes.backgroundColor === 'string' ? attributes.backgroundColor : null;

        return backgroundColor
            ? {
                  'data-background-color': backgroundColor,
                  style: `background-color: ${backgroundColor}`,
              }
            : {};
    },
};

const MemoTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...(this.parent?.() ?? {}),
            backgroundColor: backgroundColorAttribute,
        };
    },
});

const MemoTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...(this.parent?.() ?? {}),
            backgroundColor: backgroundColorAttribute,
        };
    },
});

const memoTableSelectionPluginKey = new PluginKey('memoTableSelection');

const createCellSelectionDecorations = (state: EditorState) => {
    if (!(state.selection instanceof CellSelection)) {
        return null;
    }

    const decorations: Decoration[] = [];

    state.selection.forEachCell((node, position) => {
        decorations.push(
            Decoration.node(position, position + node.nodeSize, {
                class: 'selectedCell',
            }),
        );
    });

    return DecorationSet.create(state.doc, decorations);
};

const findAncestorDepth = ($position: ResolvedPos, predicate: (node: ResolvedPos['parent']) => boolean) => {
    for (let depth = $position.depth; depth > 0; depth -= 1) {
        if (predicate($position.node(depth))) {
            return depth;
        }
    }

    return null;
};

const MemoTable = BaseTable.extend({
    addProseMirrorPlugins() {
        const parentPlugins = this.parent?.() ?? [];
        const normalizedParentPlugins = parentPlugins.map((plugin) => {
            if (plugin.spec.key !== tableEditingKey) {
                return plugin;
            }

            // prosemirror-tables can otherwise construct its selected-cell
            // DecorationSet through a separately installed prosemirror-view
            // instance. Rebuilding only that prop keeps the complete table
            // editing behavior while making all selection decorations share
            // TipTap's canonical PM view runtime.
            return new Plugin({
                ...plugin.spec,
                props: {
                    ...(plugin.spec.props ?? {}),
                    decorations: createCellSelectionDecorations,
                },
            });
        });

        return [
            ...normalizedParentPlugins,
            new Plugin({
                key: memoTableSelectionPluginKey,
                props: {
                    decorations: (state) => {
                        const { selection } = state;
                        const decorations: Decoration[] = [];

                        if (selection instanceof NodeSelection && selection.node.type.name === 'table') {
                            decorations.push(
                                Decoration.node(selection.from, selection.from + selection.node.nodeSize, {
                                    'data-memo-table-selection-state': 'table',
                                }),
                            );

                            return DecorationSet.create(state.doc, decorations);
                        }

                        const isCellSelection = selection instanceof CellSelection;
                        const $activePosition = isCellSelection
                            ? state.doc.resolve(Math.min(selection.$headCell.pos + 1, state.doc.content.size))
                            : selection.$head;
                        const cellDepth = findAncestorDepth(
                            $activePosition,
                            (node) => node.type.spec.tableRole === 'cell' || node.type.spec.tableRole === 'header_cell',
                        );
                        const tableDepth = findAncestorDepth($activePosition, (node) => node.type.name === 'table');

                        if (cellDepth === null || tableDepth === null) {
                            return null;
                        }

                        const cellFrom = $activePosition.before(cellDepth);
                        const cellNode = $activePosition.node(cellDepth);
                        const tableFrom = $activePosition.before(tableDepth);
                        const tableNode = $activePosition.node(tableDepth);
                        const selectionState =
                            isCellSelection && selection.$anchorCell.pos !== selection.$headCell.pos ? 'range' : 'cell';

                        if (isCellSelection && selectionState === 'range') {
                            const table = selection.$anchorCell.node(-1);
                            const tableStart = selection.$anchorCell.start(-1);
                            const tableMap = TableMap.get(table);
                            const selectedRectangle = tableMap.rectBetween(
                                selection.$anchorCell.pos - tableStart,
                                selection.$headCell.pos - tableStart,
                            );

                            tableMap.cellsInRect(selectedRectangle).forEach((relativeCellPosition) => {
                                const selectedCell = table.nodeAt(relativeCellPosition);

                                if (!selectedCell) {
                                    return;
                                }

                                const cellRectangle = tableMap.findCell(relativeCellPosition);
                                const rangeClasses = [
                                    'memo-table-range-cell',
                                    ...(cellRectangle.top === selectedRectangle.top ? ['memo-table-range-top'] : []),
                                    ...(cellRectangle.bottom === selectedRectangle.bottom
                                        ? ['memo-table-range-bottom']
                                        : []),
                                    ...(cellRectangle.left === selectedRectangle.left ? ['memo-table-range-left'] : []),
                                    ...(cellRectangle.right === selectedRectangle.right
                                        ? ['memo-table-range-right']
                                        : []),
                                ];
                                const selectedCellFrom = tableStart + relativeCellPosition;

                                decorations.push(
                                    Decoration.node(selectedCellFrom, selectedCellFrom + selectedCell.nodeSize, {
                                        class: rangeClasses.join(' '),
                                    }),
                                );
                            });
                        }

                        decorations.push(
                            Decoration.node(cellFrom, cellFrom + cellNode.nodeSize, {
                                class: 'memo-table-active-cell',
                            }),
                            Decoration.node(tableFrom, tableFrom + tableNode.nodeSize, {
                                'data-memo-table-selection-state': selectionState,
                            }),
                        );

                        return DecorationSet.create(state.doc, decorations);
                    },
                },
            }),
        ];
    },
});

/**
 * The canonical Memo table schema. Keep editable and read-only Memo surfaces on
 * the same extensions so persisted table HTML cannot drift between views.
 */
export const createMemoTableExtensions = (): Extensions => [
    MemoTable.configure({
        allowTableNodeSelection: true,
        cellMinWidth: 96,
        handleWidth: 5,
        lastColumnResizable: true,
        renderWrapper: true,
        resizable: true,
        HTMLAttributes: {
            class: 'memo-table',
        },
    }),
    TableRow,
    MemoTableHeader,
    MemoTableCell,
];

interface MemoTableThemeToken {
    borderRadiusLG: number;
    blue1: string;
    blue4: string;
    colorBgContainer: string;
    colorBorder: string;
    colorFillQuaternary: string;
    colorPrimary: string;
    colorPrimaryBg: string;
    colorText: string;
    colorTextQuaternary: string;
}

export const getMemoTableCSSVariables = (token: MemoTableThemeToken): CSSProperties => {
    const theme = getDocumentTableTheme(token);

    return {
        '--memo-table-background': theme.background,
        '--memo-table-border-color': theme.border,
        '--memo-table-border-radius': `${theme.borderRadius}px`,
        '--memo-table-header-background': theme.headerBackground,
        '--memo-table-header-text': theme.headerText,
        // Keep Memo table selection at parity with the List detail Glide grid.
        '--memo-table-selected-background': theme.selectedBackground,
        '--memo-table-selected-grid-color': theme.selectedGridColor,
        '--memo-table-selection-border': theme.selectionBorder,
        '--memo-table-resize-handle': token.colorPrimary,
        '--memo-table-resize-handle-color': token.colorTextQuaternary,
        '--memo-table-resize-handle-gradient': getResizeHandleGradient(token),
    } as CSSProperties;
};

export { MemoTableCell, MemoTableHeader };
